import { Component, computed, inject, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownComponent } from 'ngx-markdown';
import { ApiFinding, FindingProperties, FindingStatus, FindingType, Severity } from 'api';
import { SeverityTagComponent } from 'common/severity-tag/severity-tag.component';
import { DialogHeaderTitleDirective } from 'common/dialog/dialog-slots.directive';
import { DialogComponent } from 'common/dialog/dialog.component';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { GridTableComponent, StaticTable } from 'common/table/grid/grid-table.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { AiGeneratedContentDirective } from 'common/disclaimer/disclaimer.directive';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { FindingStateComponent } from 'component/details/finding-state/finding-state.component';
import {
  ConfirmComponent,
  ConfirmDialogAction,
  ConfirmDialogData,
} from 'common/common/confirm/confirm.component';
import { firstValueFrom } from 'rxjs';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { dirtyErrorStateMatcher, requiredAndNotWhitespaceOnly } from 'common/form/form.service';
import { EnumService } from 'common/enum/enum.service';
import { MatOption, MatSelect } from '@angular/material/select';
import { CriteriaAnalysisResultComponent } from 'component/details/requirement-intake/requirements-detail/findings/details/criteria-analysis-result.component';
import { FindingNameComponent } from 'component/details/requirement-intake/requirements-detail/findings/name/finding-name.component';
import { CWEsComponent } from 'component/details/requirement-intake/requirements-detail/findings/cwes/cwes.component';
import { ScrollableCardComponent } from 'common/card/scrollable-card/scrollable-card.component';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

export interface FindingDescriptionDialogData {
  readonly evidenceName: string | null;
  readonly finding: ApiFinding;
  readonly isAIgenerated: boolean;
}

export interface FindingDetailsDialogUpdateResult {
  properties: FindingProperties;
  statusJustification?: string;
}

export type FindingDetailsDialogResult = FindingDetailsDialogUpdateResult | 'DELETE' | null;

const TranslationKey = {
  evidenceName: 'requirementIntake.evidence.name',
  fileName: 'model.finding.properties.filename',
  severity: 'model.finding.properties.severity',
  passFailCriterion: 'model.finding.properties.passFailCriterion',
  description: 'model.finding.properties.description',
  status: 'model.finding.properties.status',
  statusJustification: 'model.finding.properties.statusJustification',
  findingDetail: 'model.finding.properties.detail',
  kev: 'model.finding.properties.kev',
  componentName: 'model.finding.properties.dependencyTrackComponentName',
  componentVersion: 'model.finding.properties.dependencyTrackComponentVersion',
  cvssV3BaseScore: 'model.finding.properties.dependencyTrackCvssV3BaseScore',
  epssScore: 'model.finding.properties.dependencyTrackEpssScore',
  epssPercentile: 'model.finding.properties.dependencyTrackEpssPercentile',
  cwes: 'model.finding.properties.dependencyTrackCwes',
  yes: 'general.yes',
  no: 'general.no',
  publishedAt: 'model.finding.properties.dependencyTrackPublished',
} as const;

type TranslationKey = (typeof TranslationKey)[keyof typeof TranslationKey];

function isManual(finding: ApiFinding) {
  return finding.type === FindingType.Manual;
}

@Component({
  selector: 'comp-evidence-finding-dialog',
  templateUrl: './evidence-finding-dialog.component.html',
  styleUrls: ['./evidence-finding-dialog.component.scss'],
  imports: [
    TranslateModule,
    MarkdownComponent,
    SeverityTagComponent,
    MatDialogClose,
    DialogHeaderTitleDirective,
    DialogComponent,
    CardButtonsComponent,
    GridTableComponent,
    NgTemplateOutlet,
    FindingStateComponent,
    ReactiveFormsModule,
    MatError,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    CriteriaAnalysisResultComponent,
    AiGeneratedContentDirective,
    FindingNameComponent,
    CWEsComponent,
    ScrollableCardComponent,
    ButtonDirective,
    IconDirective,
  ],
  providers: [DatePipe],
})
export class EvidenceFindingDialogComponent implements OnInit {
  private readonly datePipe = inject(DatePipe);

  protected readonly data = signal<FindingDescriptionDialogData>(
    inject<FindingDescriptionDialogData>(MAT_DIALOG_DATA),
  );
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly enumService = inject(EnumService);
  protected dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<EvidenceFindingDialogComponent>);

  // Content Templates
  private readonly nameTemplate = viewChild.required<TemplateRef<unknown>>('nameTemplate');
  private readonly nameEditTemplate = viewChild.required<TemplateRef<unknown>>('nameEditTemplate');
  private readonly severityTag = viewChild.required<TemplateRef<unknown>>('severityTag');
  private readonly severityEditTemplate =
    viewChild.required<TemplateRef<unknown>>('severityEditTemplate');
  private readonly findingDescription =
    viewChild.required<TemplateRef<unknown>>('findingDescription');
  private readonly findingDescriptionEditTemplate = viewChild.required<TemplateRef<unknown>>(
    'findingDescriptionEditTemplate',
  );
  private readonly status = viewChild.required<TemplateRef<unknown>>('status');
  private readonly statusJustification =
    viewChild.required<TemplateRef<unknown>>('statusJustification');
  private readonly cwesTemplate = viewChild.required<TemplateRef<unknown>>('cwesTemplate');

  readonly isEditing = signal(false);
  readonly statusChanged = signal(false);
  readonly needsSave = computed(() => this.isEditing() || this.statusChanged());

  protected readonly severityOptions = computed(() =>
    this.enumService.getEnumOptions(Severity, 'model.finding.severity'),
  );

  form!: FormGroup;

  private readonly translations = toSignal(
    this.translateService.stream(Object.values(TranslationKey)),
    {
      initialValue: {
        [TranslationKey.evidenceName]: 'Intake Requirement',
        [TranslationKey.fileName]: 'File Name',
        [TranslationKey.severity]: 'Severity',
        [TranslationKey.description]: 'Description',
      },
    },
  );

  private translate(key: TranslationKey): string {
    return this.translations()[key];
  }

  readonly tableContent = computed<StaticTable>(() => {
    const { evidenceName, finding } = this.data();
    const jsonData = finding.data ? JSON.parse(finding.data) : null;
    const passFailCriterion = jsonData?.criterion;
    const componentName = jsonData?.component?.name;
    const componentVersion = jsonData?.component?.version;
    const cvssV3BaseScore = jsonData?.vulnerability?.cvssV3BaseScore;
    const confidence = jsonData?.confidence;
    const criterionMet = jsonData?.met;
    const epssScore = jsonData?.vulnerability?.epssScore;
    const epssPercentile = jsonData?.vulnerability?.epssPercentile;
    const cwes = jsonData?.vulnerability?.cwes;
    const publishedAt = jsonData?.vulnerability?.published;
    const isEditing = this.isEditing();
    const showStatusJustification = !!finding.statusJustification;
    // 'updatedAt',by, createAt createdBy

    const entries = [
      evidenceName
        ? {
            title: this.translate(TranslationKey.evidenceName),
            content: evidenceName,
          }
        : null,
      passFailCriterion
        ? {
            title: this.translate(TranslationKey.passFailCriterion),
            content: passFailCriterion,
            row: 1,
          }
        : null,
      !passFailCriterion && (finding.name || isEditing)
        ? {
            title: this.translate(TranslationKey.findingDetail),
            contentTemplate:
              isEditing && isManual(finding) ? this.nameEditTemplate() : this.nameTemplate(),
            row: 1,
          }
        : null,
      finding.kev !== undefined
        ? {
            title: this.translate(TranslationKey.kev),
            content: this.translate(finding.kev ? TranslationKey.yes : TranslationKey.no),
            row: 1,
          }
        : null,
      publishedAt !== undefined
        ? {
            title: this.translate(TranslationKey.publishedAt),
            content: this.datePipe.transform(publishedAt, 'shortDate'),
            row: 1,
          }
        : null,
      finding.filename
        ? {
            title: this.translate(TranslationKey.fileName),
            content: finding.filename,
            row: 2,
          }
        : null,
      finding.severity || isEditing
        ? {
            title: this.translate(TranslationKey.severity),
            contentTemplate:
              isEditing && isManual(finding) ? this.severityEditTemplate() : this.severityTag(),
            row: 2,
          }
        : null,
      finding.status
        ? {
            title: this.translate(TranslationKey.status),
            contentTemplate: this.status(),
            row: 2,
          }
        : null,
      showStatusJustification
        ? {
            title: this.translate(TranslationKey.statusJustification),
            contentTemplate: this.statusJustification(),
            row: 3,
          }
        : null,
      componentName
        ? {
            title: this.translate(TranslationKey.componentName),
            content: componentName,
            row: 4,
          }
        : null,
      componentVersion
        ? {
            title: this.translate(TranslationKey.componentVersion),
            content: componentVersion,
            row: 4,
          }
        : null,
      cvssV3BaseScore || componentVersion
        ? {
            title: this.translate(TranslationKey.cvssV3BaseScore),
            content: cvssV3BaseScore ?? '-',
            row: 4,
          }
        : null,
      epssScore
        ? {
            title: this.translate(TranslationKey.epssScore),
            content: epssScore,
            row: 5,
          }
        : null,
      epssPercentile
        ? {
            title: this.translate(TranslationKey.epssPercentile),
            content: epssPercentile,
            row: 5,
          }
        : null,
      cwes
        ? {
            title: this.translate(TranslationKey.cwes),
            contentTemplate: this.cwesTemplate(),
            row: 5,
          }
        : null,
      finding.description || isEditing
        ? {
            title: this.translate(TranslationKey.description),
            contentTemplate:
              isEditing && isManual(finding)
                ? this.findingDescriptionEditTemplate()
                : this.findingDescription(),
            contentTemplateContext: { confidence: confidence, criterionMet: criterionMet },
            row: 6,
          }
        : null,
    ].filter(<T>(entry: T | null): entry is T => entry !== null);

    return { entries };
  });

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: [this.data().finding.name, [requiredAndNotWhitespaceOnly, Validators.maxLength(255)]],
      description: [this.data().finding.description],
      severity: [this.data().finding.severity],
      status: [this.data().finding.status],
      statusJustification: [this.data().finding.statusJustification, [Validators.maxLength(1024)]],
    });
  }

  protected get nameControl(): FormControl {
    return this.form.get('name') as FormControl;
  }

  protected get descriptionControl(): FormControl {
    return this.form.get('description') as FormControl;
  }

  protected get severityControl(): FormControl {
    return this.form.get('severity') as FormControl;
  }

  protected get statusControl(): FormControl {
    return this.form.get('status') as FormControl;
  }

  protected get statusJustificationControl(): FormControl {
    return this.form.get('statusJustification') as FormControl;
  }

  protected onEdit() {
    this.resetForm();
    this.isEditing.set(true);
  }

  protected onCancelEdit() {
    this.resetForm();
    this.isEditing.set(false);
  }

  protected async onDelete() {
    const dialogRef = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(
      ConfirmComponent,
      {
        data: {
          confirmDialogAction: ConfirmDialogAction.DELETE,
          items: [this.translateService.instant('requirementIntake.details.findings.thisFinding')],
        },
      },
    );
    const isConfirmed = await firstValueFrom(dialogRef.afterClosed());
    if (isConfirmed) {
      this.dialogRef.close('DELETE');
    }
  }

  protected onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close({
      properties: {
        name: this.nameControl.value,
        description: this.descriptionControl.value,
        severity: this.severityControl.value,
        status: this.statusControl.value,
      },
      statusJustification: this.statusJustificationControl.value ?? undefined,
    });
  }

  private resetForm(): void {
    this.form.reset({
      name: this.data().finding.name,
      description: this.data().finding.description,
      severity: this.data().finding.severity,
      status: this.data().finding.status,
      statusJustification: this.data().finding.statusJustification,
    });
  }

  private needsJustification(previousStatus: FindingStatus, newStatus?: FindingStatus): boolean {
    if (!newStatus || newStatus === previousStatus) {
      return false;
    }

    // According to EMR this is the only state transition where no justification is needed.
    return !(
      newStatus === FindingStatus.Unresolved && previousStatus === FindingStatus.NotApplicable
    );
  }

  protected readonly dirtyErrorStateMatcher = dirtyErrorStateMatcher;
  protected readonly FindingStatus = FindingStatus;

  protected onStatusChanged(updatedFinding: ApiFinding) {
    if (this.needsJustification(this.data().finding.status, updatedFinding.status)) {
      this.form.controls['statusJustification'].setValue(updatedFinding.statusJustification);
    } else {
      this.form.controls['statusJustification'].setValue(null);
    }
    this.data.update((olddata) => {
      return {
        ...olddata,
        finding: updatedFinding,
      };
    });
    this.form.controls['status'].setValue(updatedFinding.status);
    this.statusChanged.set(true);
  }
}
